import { useEffect, useState, useContext } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { graphqlFetch } from '@shared/api';
import { queryKeys } from '@shared/api/keys';
import type { CacheArgs } from '@shared/api/queries';
import { collectionQueryDocument } from './collection.document';

import config from '@/config';
import { AppContext } from '@/app.context';
import { isNumeric, Issue, USER_ROLE } from '@/constants';
import { useIssueNotFound } from '@shared/lib/hooks';
import { getQueryCacheOptions } from '@shared/query-client/query-client';

/**
 * `queryOptions` factory, not a bare `useQuery` call inline in the hook --
 * same reason `@shared/api/queries.ts`'s `previewIssueOptions` exists: it's
 * the one thing both `useGetCollection` (below) and `app.router.tsx`'s
 * `collections/$id` route `loader` need to agree on (same query key, same
 * fetcher) for a route-level `ensureQueryData` prefetch to actually land in
 * the same cache slot `useGetCollection` reads from. Kept local to this
 * template's own `collection/` directory rather than added to the shared,
 * cross-template `queries.ts` -- collections are morningstar-exclusive (no
 * other template has this route), so this isn't a shared-entity factory in
 * the sense that file's own scope note describes.
 *
 * No offline-specific branching (unlike `previewIssueOptions`'s
 * `fetchPreviewIssue`, which deliberately throws instead of caching a
 * placeholder while offline): `useGetCollection` never had that concern
 * either -- it always just attempted the fetch and surfaced whatever
 * `useQuery`'s own `error`/`isPending` state produced, rendered inline by
 * `collection.tsx`'s `View()`. This factory preserves that exact behavior;
 * it isn't a redesign of the error handling, just a shared home for the
 * same query.
 */
export function collectionOptions(id: string, args: CacheArgs) {
	return queryOptions({
		queryKey: queryKeys.collectionIssue(id),
		queryFn: () =>
			graphqlFetch(collectionQueryDocument, {
				id,
				isSlug: !isNumeric(id),
			}) as unknown as Promise<CollectionQuery>,
		...getQueryCacheOptions(args),
	});
}

export function useGetCollection(id: string): GetCollectionResponse {
	const {
		parseGraphQLClientError,
		onlineStatus: isOnline,
	} = useContext(AppContext);
	const [issue, setIssue] = useState<Issue | null>();

	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';

	// Cast: same as Preview.hooks.ts's PreviewIssue migration -- the
	// generated type's nullability is honest where the shared Issue type
	// claims required fields; reconciling that is topic 5's "derive
	// Issue/Article/Style from generated types" step. Behaviour unchanged.
	const { isPending, error, data } = useQuery({
		...collectionOptions(id, {
			isOnline,
			isAdmin,
			ttlMinutes: config.app?.cache?.ttl?.getIssue,
		}),
	});

	useIssueNotFound(issue);

	useEffect(() => {
		if (!data) return;

		const { issue } = data;
		setIssue(issue);
	}, [data]);

	return {
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
		issue,
	};
}

interface CollectionQuery {
	issue: Issue | null;
}

interface GetCollectionResponse {
	isLoading: boolean;
	error: Error | null;
	issue?: Issue | null;
}
